import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete, Edit, Send } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { styled } from '@mui/material/styles';
import NotificationApi from '../../api/notifications';
import { NotificationPriority, NotificationType } from '../../types/notification';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-popper': {
    zIndex: 9999,
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker__header': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .react-datepicker__day--selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '16.5px 14px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: theme.typography.fontFamily,
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    }
  }
}));

interface Announcement {
  _id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  icon?: string;
  actionUrl?: string;
  actionLabel?: string;
  targetAudience: 'all' | 'counsellors' | 'admins';
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  active: boolean;
}

const GlobalAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [readReceipts, setReadReceipts] = useState<any[]>([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    type: 'announcement' | 'policy_update' | 'training' | 'maintenance';
    priority: 'low' | 'medium' | 'high';
    title: string;
    message: string;
    icon: string;
    actionUrl: string;
    actionLabel: string;
    targetAudience: 'all' | 'active_counsellors' | 'suspended' | 'non_compliant';
    expiresAt: Date;
  }>({
    type: 'announcement',
    priority: 'medium',
    title: '',
    message: '',
    icon: '',
    actionUrl: '',
    actionLabel: '',
    targetAudience: 'all',
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Default 7 days
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await NotificationApi.getAnnouncements();
      setAnnouncements(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (announcement?: Announcement) => {
    if (announcement) {
      setEditingId(announcement._id);
      // Only allow editing announcement types that are valid for global announcements
      const validType = ['announcement', 'policy_update', 'training', 'maintenance'].includes(announcement.type)
        ? (announcement.type as 'announcement' | 'policy_update' | 'training' | 'maintenance')
        : 'announcement';
      const validPriority = announcement.priority === 'critical' ? 'high' : announcement.priority as 'low' | 'medium' | 'high';
      const validAudience = announcement.targetAudience === 'counsellors' || announcement.targetAudience === 'admins'
        ? 'all'
        : announcement.targetAudience as 'all' | 'active_counsellors' | 'suspended' | 'non_compliant';
      
      setFormData({
        type: validType,
        priority: validPriority,
        title: announcement.title,
        message: announcement.message,
        icon: announcement.icon || '',
        actionUrl: announcement.actionUrl || '',
        actionLabel: announcement.actionLabel || '',
        targetAudience: validAudience,
        expiresAt: new Date(announcement.expiresAt)
      });
    } else {
      setEditingId(null);
      setFormData({
        type: 'announcement',
        priority: 'medium',
        title: '',
        message: '',
        icon: '',
        actionUrl: '',
        actionLabel: '',
        targetAudience: 'all',
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      await NotificationApi.createAnnouncement({
        ...formData,
        expiresAt: formData.expiresAt.toISOString()
      });
      await fetchAnnouncements();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to create announcement');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }
    try {
      // await NotificationApi.deleteAnnouncement(id);
      await fetchAnnouncements();
    } catch (err: any) {
      setError(err.message || 'Failed to delete announcement');
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const capitalizeLabel = (text: string): string => {
    if (text === 'all') return 'All Users';
    if (text === 'active_counsellors') return 'Active Counsellors';
    if (text === 'suspended') return 'Suspended';
    if (text === 'non_compliant') return 'Non-Compliant';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const handleCardClick = async (announcementId: string) => {
    setSelectedAnnouncementId(announcementId);
    setTrackingDialogOpen(true);
    setLoadingReceipts(true);
    try {
      const response = await NotificationApi.getAnnouncementReadReceipts(announcementId);
      setReadReceipts(response);
    } catch (err: any) {
      console.error('Error fetching read receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const handleCloseTrackingDialog = () => {
    setTrackingDialogOpen(false);
    setSelectedAnnouncementId(null);
    setReadReceipts([]);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2" className="theme-text-primary">
                Global Announcement
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Announcement
              </Button>
            </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Global announcements are shown to all users matching the target audience. They appear in the notification bell and as sticky banners for high/critical priority.
      </Alert>

      {/* Announcements List */}
      <Grid container spacing={2}>
        {announcements.map((announcement) => (
          <Grid item xs={12} md={6} key={announcement._id}>
            <Card
              onClick={() => handleCardClick(announcement._id)}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={capitalizeLabel(announcement.priority)}
                      color={getPriorityColor(announcement.priority)}
                      size="small"
                    />
                    <Chip
                      label={capitalizeLabel(announcement.targetAudience)}
                      size="small"
                      variant="outlined"
                    />
                    {announcement.icon && (
                      <Typography sx={{ fontSize: 20 }}>{announcement.icon}</Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(announcement);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(announcement._id);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {announcement.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {announcement.message}
                </Typography>

                {announcement.actionUrl && (
                  <Chip
                    label={announcement.actionLabel || 'View'}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}

                <Typography variant="caption" color="text.secondary" display="block">
                  Expires: {new Date(announcement.expiresAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {announcements.length === 0 && !loading && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No announcements yet. Create your first one!
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editingId ? 'Edit Announcement' : 'Create Global Announcement'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High (Sticky Banner)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={formData.targetAudience}
                label="Target Audience"
                onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value as any })}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="active_counsellors">Active Counsellors</MenuItem>
                <MenuItem value="suspended">Suspended Counsellors</MenuItem>
                <MenuItem value="non_compliant">Non-Compliant Counsellors</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              multiline
              rows={4}
              required
            />

            <TextField
              fullWidth
              label="Icon (emoji)"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="📢"
              helperText="Optional: Add an emoji icon"
            />

            <TextField
              fullWidth
              label="Action URL (optional)"
              value={formData.actionUrl}
              onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
              placeholder="/policies"
              helperText="Optional: Link users can click"
            />

            {formData.actionUrl && (
              <TextField
                fullWidth
                label="Action Button Label"
                value={formData.actionLabel}
                onChange={(e) => setFormData({ ...formData, actionLabel: e.target.value })}
                placeholder="View Details"
              />
            )}

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Expires At
              </Typography>
              <DatePickerWrapper>
                <DatePicker
                  selected={formData.expiresAt}
                  onChange={(date: Date | null) => setFormData({ ...formData, expiresAt: date || new Date() })}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="Select expiry date and time"
                  minDate={new Date()}
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </DatePickerWrapper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Announcement will automatically expire after this date
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<Send />}
            disabled={!formData.title || !formData.message}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)'
              }
            }}
          >
            {editingId ? 'Update' : 'Create'} Announcement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read Tracking Dialog */}
      <Dialog
        open={trackingDialogOpen}
        onClose={handleCloseTrackingDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Announcement Read Tracking
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingReceipts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : readReceipts.length === 0 ? (
            <Alert severity="info">
              No one has read this announcement yet.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {readReceipts.length} {readReceipts.length === 1 ? 'counsellor has' : 'counsellors have'} read this announcement
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {readReceipts.map((receipt: any) => (
                  <Card key={receipt._id} variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {receipt.user?.firstName} {receipt.user?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {receipt.user?.email}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Read: {new Date(receipt.readAt).toLocaleString()}
                          </Typography>
                          {receipt.dismissed && (
                            <Chip 
                              label="Dismissed" 
                              size="small" 
                              color="default" 
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTrackingDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalAnnouncements;
